import React, { useState, useContext, useEffect } from "react";

import { OutlinedInput, Button, CircularProgress } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddCircleIcon from "@material-ui/icons/AddCircle";

import {
  MODAL_TYPES,
  TEMPLATE_KEYS,
  StatisticsConfig,
  MAX_STATISTICS_TITLE,
  DEFAULT_IMAGE_PARAMS,
  SIDEBAR_SECTIONS,
  SECTIONKEY_TO_MISC_KEY,
  SIDEBAR_KEYS,
} from "../../../../constants";
import api from "../../../../../../../data/APIs";

import style from "./style.module.css";
import baseStyle from "../../style.module.css";

import { WebpageCreationContext } from "../../../..";

import { getBackgroundImageParams } from "ui/pages/TemplatePreview/utils";
import { BackgroundImage } from "../Unlock/StatsSection/BackgroundImage";
import ExlyImage from "features/Common/modules/ExlyImage/ExlyImage";
import { is_empty } from "utils/validations";

import { creator_miscellaneous_data_types } from "features/Common/modules/CreatorData/CreatorData.constants";
import SectionVisibilityToggle from "../hideShow";
import { handleVisibilityToggle } from "../../utils";
import useVisibilityMutations from "../../useVisibilityMutations.hook";

const StatisticCard = ({ handleEdit, handleDelete, item, templateName }) => (
  <div className={`${style.card} ${style?.[`${templateName}Template`]}`}>
    <div className={style.controls}>
      <span title="Edit" className={style.controlIcon}>
        <EditIcon onClick={() => handleEdit(item)} />
      </span>
      <span title="Delete" className={style.controlIcon}>
        <DeleteIcon onClick={() => handleDelete(item)} />
      </span>
    </div>
    {StatisticsConfig.showImageInTemplates.includes(templateName) ? (
      <ExlyImage
        className={`${style.cardImage} ${style?.[`${templateName}Template`]}`}
        src={item.media}
        alt=""
      />
    ) : null}
    <div className={`${style.content} ${style?.[`${templateName}Template`]}`}>
      <div className={`${style.title} ${style?.[`${templateName}Template`]}`}>
        {item.value}
      </div>
      <div
        className={`${style.description} ${style?.[`${templateName}Template`]}`}
      >
        {item.description}
      </div>
    </div>
  </div>
);

export const Statistics = (props) => {
  const {
    fetchCall,
    pushNotif,
    templateData,
    disableAllEdits,
    statistics: { statisticsData, setStatisticsData },
    modal: { openModal, closeModal },
    miscellaneous_data,
    fetchMiscellaneousData,
    set_miscellaneous_data,
  } = useContext(WebpageCreationContext);

  const [isSaving, setSaving] = useState(false);
  const [errorObject, setErrorObject] = React.useState({});
  const [visible, set_visible] = React.useState(true);

  // Mutations for visibility toggle
  const { createMutation, updateMutation } = useVisibilityMutations();

  const maxItemsCreated =
    statisticsData?.length >=
    (StatisticsConfig?.maxItems[templateData.slug] || 4);

  const { state, setField } = props;
  const splicedStatisticsData =
    statisticsData.length > StatisticsConfig?.maxItems[templateData?.slug]
      ? statisticsData?.slice(0, StatisticsConfig?.maxItems[templateData.slug])
      : statisticsData;

  const handleCreate = () => {
    if (maxItemsCreated) {
      pushNotif(
        `You can only add upto ${
          StatisticsConfig?.maxItems[templateData.slug] || 4
        } items`
      );
      return;
    }

    openModal(MODAL_TYPES.STATISTICS, {
      modalLabels: {
        modalTitle: "Add new item",
        showDividers: true,
      },
      data: {},
      template: templateData.slug,
      primaryAction: ({ data: createData }) => {
        setSaving("Creating");
        closeModal();
        fetchCall({
          url: api.create_statistic,
          method: "POST",
          payload: {
            statistic_item: {
              ...createData,
            },
          },
          successCallback: (respData) => {
            pushNotif("Item created");
            setStatisticsData([
              ...statisticsData,
              { ...createData, uuid: respData.uuid },
            ]);
            setSaving(null);
          },
          errorCallback: (err) => {
            pushNotif(err?.body?.message || "Error while creating item");
            setSaving(null);
          },
        });
      },
      secondaryAction: closeModal,
    });
  };

  const handleEdit = (item) => {
    openModal(MODAL_TYPES.STATISTICS, {
      modalLabels: {
        modalTitle: "Edit item",
        showDividers: true,
      },
      template: templateData.slug,
      data: item,
      primaryAction: ({ data: updatedData }) => {
        setSaving("Updating");
        closeModal();
        fetchCall({
          url: api.update_statistic,
          method: "POST",
          payload: {
            uuid: item.uuid,
            statistic_item: {
              ...updatedData,
            },
          },
          successCallback: () => {
            pushNotif("Item updated");
            setStatisticsData([
              ...statisticsData.map((t) =>
                t.uuid === item.uuid ? updatedData : t
              ),
            ]);
            setSaving(null);
          },
          errorCallback: (err) => {
            pushNotif(err?.body?.message || "Error while updating item");
            setSaving(null);
          },
        });
      },
      secondaryAction: closeModal,
    });
  };

  const handleDelete = (item) => {
    openModal(MODAL_TYPES.CONFIRM, {
      modalLabels: {
        modalTitle: "Confirm Action",
        primaryText: "Cancel Delete",
        secondaryText: "Delete",
        showDividers: false,
      },
      body: `Are you sure you want to delete this item?`,
      hideCtaIcons: true,
      primaryAction: closeModal,
      secondaryAction: () => {
        setSaving("Deleting");
        closeModal();
        fetchCall({
          url: api.delete_statistic,
          method: "POST",
          payload: {
            uuid: item.uuid,
          },
          successCallback: () => {
            pushNotif("Item deleted successfully!");
            setStatisticsData(
              statisticsData.filter((i) => i.uuid !== item.uuid)
            );
            setSaving(null);
          },
          errorCallback: (error) => {
            pushNotif(error?.body?.message || "Error while deleting item");
            setSaving(null);
          },
        });
      },
    });
  };

  useEffect(() => {
    try {
      if (is_empty(miscellaneous_data?.statistics)) {
        set_visible(true);
        return;
      }

      const temp = miscellaneous_data.statistics[0];

      set_visible(
        typeof temp?.data?.visible === "boolean" ? temp.data.visible : true
      );
    } catch {
      set_visible(true);
    }
  }, [miscellaneous_data]);

  useEffect(() => {
    setErrorObject({});
  }, [isSaving]);

  const onToggleVisibility = () =>
    handleVisibilityToggle({
      currentVisible: visible,
      setVisible: set_visible,
      miscellaneousData: miscellaneous_data,
      sectionKey: SECTIONKEY_TO_MISC_KEY[SIDEBAR_KEYS.STATISTICS],
      sectionType: creator_miscellaneous_data_types.STATS,
      defaultSectionName: SIDEBAR_SECTIONS[SIDEBAR_KEYS.STATISTICS].title,
      fetchMiscellaneousData,
      set_miscellaneous_data,
      createMutation: createMutation,
      updateMutation: updateMutation,
    });

  if (!statisticsData) {
    return <div>Loading...</div>;
  }

  const imageParams = getBackgroundImageParams(
    state,
    TEMPLATE_KEYS.UNLOCK,
    DEFAULT_IMAGE_PARAMS[TEMPLATE_KEYS.UNLOCK].width
  );
  return (
    <>
      <SectionVisibilityToggle
        visible={visible}
        disabled={disableAllEdits}
        onToggle={onToggleVisibility}
      />

      <div className={baseStyle.fieldContainer}>
        <BackgroundImage
          state={state}
          setField={setField}
          slug_value={state?.template?.slug}
          imageParams={imageParams}
          disableAllEdits={disableAllEdits}
        />

        {templateData.slug === TEMPLATE_KEYS.YOGA ? null : (
          <>
            <div className={baseStyle.label}>
              What would you like to call this section?
            </div>
            <div className={baseStyle.field}>
              <OutlinedInput
                placeholder="Enter the section heading here"
                className={`${baseStyle.input} ${
                  style.statisticsSectionTitle
                } ${style?.[`${templateData.slug}Template`]}`}
                multiline
                rows={2}
                value={state?.custom_headings?.statistics_title}
                error={errorObject?.sectionNameLength}
                inputProps={{
                  maxLength: MAX_STATISTICS_TITLE,
                }}
                onChange={(e) => {
                  const value = e?.target?.value;
                  if (value?.length >= MAX_STATISTICS_TITLE) {
                    setErrorObject((prev) => ({
                      ...prev,
                      sectionNameLength: `Maximum field length of ${MAX_STATISTICS_TITLE} reached.`,
                    }));
                  } else {
                    setErrorObject((prev) => ({
                      ...prev,
                      sectionNameLength: false,
                    }));
                  }
                  setField("custom_headings", {
                    ...state?.custom_headings,
                    statistics_title: e?.target?.value || "",
                  });
                }}
              />
            </div>
          </>
        )}

        <div className={baseStyle.error}>
          {errorObject?.sectionNameLength}
          <br />
          <br />
          <br />
        </div>

        <div className={style.cardList}>
          {(splicedStatisticsData || []).map((item) => (
            <StatisticCard
              key={item?.value}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              item={item}
              templateName={templateData.slug}
            />
          ))}
          {splicedStatisticsData && splicedStatisticsData.length === 0 ? (
            <div className={style.info}>
              Add items from below to preview them here
            </div>
          ) : null}
        </div>

        {isSaving ? (
          <div className={style.savingStatus}>
            {isSaving}&nbsp;
            <CircularProgress color="secondary" size={24} thickness={2} />
          </div>
        ) : null}

        <Button
          className={style.addButton}
          color="primary"
          disabled={isSaving}
          title={
            maxItemsCreated
              ? `You can only add upto ${
                  StatisticsConfig?.maxItems[templateData.slug] || 4
                } statistic items`
              : null
          }
          onClick={handleCreate}
        >
          <AddCircleIcon className={style.addIcon} /> Add new item
        </Button>
      </div>
    </>
  );
};
